import { useEffect, useState } from "react";
import { fornitoreIndicato } from "@app/schema";
import type { DataServices } from "./DataProvider";
import type { Richiesta } from "./richieste";

/**
 * Fornitori per il gestore. L'elenco "Fornitori manutenzione" non è leggibile dai
 * coordinatori: va caricato solo nelle pagine del gestore.
 */

export interface FornitoreScelta {
  id: number;
  nome: string;
  referente?: string;
  telefono?: string;
  email?: string;
  note?: string;
  attivo: boolean;
  categorie: string[];
  /** vuoto = tutte le sedi */
  sedi: string[];
  /** vuoto = tutti i servizi */
  servizi: string[];
}

async function carica(s: DataServices): Promise<FornitoreScelta[]> {
  // Nomi di sedi, servizi e categorie (anche disattivati: servono per leggere i collegamenti)
  const nomi = async (repo: DataServices["sedi"] | DataServices["servizi"] | DataServices["categorie"]) =>
    new Map((await repo.queryAll({ top: 500 })).map((i) => [i.id, i.fields.Title?.trim() ?? ""]));
  const [items, sedi, servizi, categorie] = await Promise.all([
    s.fornitori.queryAll({ top: 2000 }),
    nomi(s.sedi),
    nomi(s.servizi),
    nomi(s.categorie),
  ]);
  const a = (ids: number[] | undefined, m: Map<number, string>) =>
    (ids ?? []).map((id) => m.get(id)).filter((x): x is string => !!x);
  return items
    .filter((i) => i.fields.Title?.trim())
    .map((i) => ({
      id: i.id,
      nome: i.fields.Title!.trim(),
      referente: i.fields.Referente?.trim() || undefined,
      telefono: i.fields.Telefono?.trim() || undefined,
      email: i.fields.Email?.trim() || undefined,
      note: i.fields.Note?.trim() || undefined,
      attivo: i.fields.Attivo !== false,
      categorie: a(i.fields.Categorie, categorie),
      sedi: a(i.fields.Sedi, sedi),
      servizi: a(i.fields.Servizi, servizi),
    }))
    .sort((x, y) => x.nome.localeCompare(y.nome, "it"));
}

const cache = new WeakMap<DataServices, Promise<FornitoreScelta[]>>();

export function loadFornitori(s: DataServices): Promise<FornitoreScelta[]> {
  let p = cache.get(s);
  if (!p) {
    p = carica(s);
    p.catch(() => cache.delete(s));
    cache.set(s, p);
  }
  return p;
}

export function useFornitori(s: DataServices, enabled = true) {
  const [fornitori, setFornitori] = useState<FornitoreScelta[]>();
  const [error, setError] = useState<unknown>();
  useEffect(() => {
    if (enabled) loadFornitori(s).then(setFornitori, setError);
  }, [s, enabled]);
  return { fornitori, error };
}

/**
 * Fornitori per una richiesta: prima quelli indicati per categoria, sede e servizio,
 * poi gli altri attivi. Il fornitore già scelto resta sempre tra le opzioni.
 */
export function fornitoriPer(tutti: FornitoreScelta[], r: Richiesta, sceltoId?: number) {
  const f = r.fields;
  const req = { categoria: f.Categoria, sede: f.Sede || f.Struttura, servizio: f.Servizio };
  const visibili = tutti.filter((x) => x.attivo || x.id === sceltoId);
  const indicati = visibili.filter((x) => fornitoreIndicato(x, req));
  const altri = visibili.filter((x) => !indicati.includes(x));
  return { indicati, altri };
}

/** Descrizione breve dell'ambito di un fornitore (per l'elenco "altri"). */
export function ambito(x: FornitoreScelta): string {
  const parti = [x.categorie.join(", ") || "nessuna categoria", x.sedi.length ? x.sedi.join(", ") : "tutte le sedi"];
  if (x.servizi.length) parti.push(x.servizi.join(", "));
  return parti.join(" · ");
}
